<template>
  <v-dialog
    v-model="isOpen"
    max-width="500px"
    scroll-strategy="block"
    :retain-focus="!isMobile && !isTouchDevice"
    @click:outside="closeDialog"
  >
    <div class="bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto mobile-dialog-scroll mobile-safe-area">
      <!-- Close Button -->
      <button 
        @click="closeDialog" 
        @touchend.prevent="closeDialog"
        class="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors z-10 touch-target"
        aria-label="Close dialog"
      >
        <v-icon>mdi-close</v-icon>
      </button>

      <!-- Header -->
      <div class="text-center p-8 animate-fade-in">
        <img 
          src="/ducksImages/duckIcon.webp" 
          class="w-24 h-24 mx-auto mb-6 drop-shadow-lg transition-transform" 
          :class="isRegisterMode ? 'animate-float-slow' : 'animate-bounce-slow'"
        />
        <h2 class="text-3xl font-bold text-gray-700  slide-in-bottom">
          {{ isRegisterMode ? 'Welcome Aboard! 🚢' : 'Welcome Back! 🦆' }}
        </h2>
        <p class="mt-2 text-gray-600  animate-fade-in" style="animation-delay: 0.2s">
          {{ isRegisterMode ? 'Join the duck-finding adventure' : 'Continue your journey' }}
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" 
        class="mx-6 mb-4 bg-green-50  text-green-600  text-sm rounded-lg p-4 text-center animate-pulse-slow"
      >
        {{ successMessage }}
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="p-8 pt-0 space-y-6">
        <!-- Email Field -->
        <div class="form-field animate-fade-in" style="animation-delay: 0.3s">
          <label class="block text-sm font-medium text-gray-700">Email</label>
          <input
            ref="emailInputRef"
            v-model="formData.email"
            type="email"
            required
            class="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 
                  bg-white text-gray-900 
                  shadow-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="captain@ducknseek.com"
            :class="{'border-red-500 focus:border-red-500 focus:ring-red-500/20': emailError}"
            @input="validateEmail"
          />
          <p v-if="emailError" class="mt-1 text-sm text-red-500 slide-in-left">{{ emailError }}</p>
        </div>

        <!-- Username Field (Registration only) -->
        <div v-if="isRegisterMode" class="form-field animate-fade-in" style="animation-delay: 0.35s">
          <label class="block text-sm font-medium text-gray-700">Username</label>
          <input
            v-model="formData.username"
            type="text"
            required
            minlength="3"
            maxlength="20"
            class="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 
                  bg-white text-gray-900 
                  shadow-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="captain_duck"
            :class="{'border-red-500 focus:border-red-500 focus:ring-red-500/20': usernameError}"
            @input="validateUsername"
          />
          <p v-if="usernameError" class="mt-1 text-sm text-red-500 slide-in-left">{{ usernameError }}</p>
        </div>

        <!-- Password Field -->
        <div class="form-field animate-fade-in" style="animation-delay: 0.4s">
          <label class="block text-sm font-medium text-gray-700 ">Password</label>
          <div class="relative">
            <input
              v-model="formData.password"
              :type="showPassword ? 'text' : 'password'"
              required
              :minlength="isRegisterMode ? 8 : undefined"
              class="mt-1 block w-full rounded-lg border border-gray-300  px-4 py-3 
                    bg-white  text-gray-900 
                    shadow-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="••••••••"
              :class="{'border-red-500 focus:border-red-500 focus:ring-red-500/20': passwordError}"
              @input="validatePassword"
            />
            <button 
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700   transition-colors"
              @click="showPassword = !showPassword"
            >
              <v-icon>{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </button>
          </div>
          <p v-if="passwordError" class="mt-1 text-sm text-red-500 slide-in-left">{{ passwordError }}</p>
        </div>

        <!-- Confirm Password Field (Registration only) -->
        <div v-if="isRegisterMode" class="form-field animate-fade-in" style="animation-delay: 0.5s">
          <label class="block text-sm font-medium text-gray-700 ">Confirm Password</label>
          <div class="relative">
            <input
              v-model="formData.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              required
              minlength="8"
              class="mt-1 block w-full rounded-lg border border-gray-300  px-4 py-3 
                    bg-white  text-gray-900 
                    shadow-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="••••••••"
              :class="{'border-red-500 focus:border-red-500 focus:ring-red-500/20': confirmPasswordError}"
              @input="validateConfirmPassword"
            />
            <button 
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700   transition-colors"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <v-icon>{{ showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </button>
          </div>
          <p v-if="confirmPasswordError" class="mt-1 text-sm text-red-500 slide-in-left">{{ confirmPasswordError }}</p>
        </div>

        <!-- Error Display -->
        <div v-if="loginError" 
          class="bg-red-50 text-red-600 text-sm rounded-lg p-4 text-center shake-animation"
        >
          <p>{{ loginError }}</p>
          <div v-if="showConfirmationOptions" class="mt-2">
            <v-btn
              variant="text"
              size="small"
              color="primary"
              :loading="isResendingConfirmation"
              @click="handleResendConfirmation"
            >
              Resend Confirmation Email
            </v-btn>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-4 animate-fade-in" style="animation-delay: 0.6s">
          <v-btn
            block
            color="primary"
            size="large"
            :loading="isLoggingIn"
            type="submit"
            class="rounded-lg hover:scale-102 transition-transform"
            :disabled="isSubmitDisabled"
          >
            <span class="relative">
              {{ isLoggingIn ? 'Loading...' : getButtonText() }}
            </span>
          </v-btn>

          <div class="sm:flex sm:justify-between sm:items-center">
            <button
              type="button"
              @click="toggleAuthMode"
              class="w-full text-center sm:text-left text-sm text-primary hover:text-primary-dark transition-colors hover:underline py-2 px-1"
            >
              {{ isRegisterMode ? 'Already have an account? Login' : 'Need an account? Register' }}
            </button>
            
            <!-- Duck separator - only visible on mobile -->
            <div v-if="!isRegisterMode" class="flex justify-center sm:hidden my-1">
              <img src="/ducksImages/duckIcon.webp" alt="Duck separator" class="w-6 h-6 opacity-50" />
            </div>
            
            <button
              type="button"
              v-if="!isRegisterMode"
              @click="showForgotPasswordDialog"
              class="w-full text-center sm:text-right text-sm text-primary hover:text-primary-dark transition-colors hover:underline py-2 px-1"
            >
              Forgot Password?
            </button>
          </div>
        </div>
      </form>
    </div>
  </v-dialog>

  <!-- Waiting Confirmation Dialog -->
  <v-dialog
    v-model="showWaitingConfirmation"
    max-width="500px"
    scroll-strategy="block"
    :retain-focus="!isMobile && !isTouchDevice"
    @click:outside="() => showWaitingConfirmation = false"
  >
    <div class="bg-white rounded-xl shadow-xl p-8 text-center max-h-[90vh] overflow-y-auto mobile-dialog-scroll mobile-safe-area">
      <!-- Add close button at top right -->
      <button 
        @click="showWaitingConfirmation = false" 
        @touchend.prevent="showWaitingConfirmation = false"
        class="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors touch-target"
        aria-label="Close dialog"
      >
        <v-icon>mdi-close</v-icon>
      </button>
      
      <img src="/ducksImages/duckIcon.webp" class="w-24 h-24 mx-auto mb-6 animate-float" />
      <h2 class="text-2xl font-bold mb-4 text-gray-900  animate-fade-in">Check Your Email! 📧</h2>
      <p class="text-gray-600  mb-6 animate-fade-in" style="animation-delay: 0.2s">
        We've sent a confirmation email to:<br>
        <span class="font-semibold text-primary">{{ formData.email }}</span>
      </p>
      <div class="message-bubble mb-6 animate-fade-in" style="animation-delay: 0.4s">
        <p class="text-gray-500  text-sm">
          Please check your inbox and click the confirmation link to activate your account.
        </p>
      </div>
      <div class="space-y-4 animate-fade-in" style="animation-delay: 0.6s">
        <v-btn
          ref="waitingBackButtonRef"
          block
          variant="outlined"
          @click="backToLogin"
          class="transition-transform hover:scale-102"
        >
          Back to Login
        </v-btn>
        <v-btn
          block
          color="primary"
          :loading="isResendingConfirmation"
          @click="handleResendConfirmation"
          class="transition-transform hover:scale-102"
        >
          Resend Confirmation Email
        </v-btn>
      </div>
    </div>
  </v-dialog>

  <!-- Forgot Password Dialog -->
  <v-dialog
    v-model="showForgotPassword"
    max-width="500px"
    scroll-strategy="block"
    :retain-focus="!isMobile && !isTouchDevice"
    @click:outside="() => showForgotPassword = false"
  >
    <div class="bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto mobile-dialog-scroll mobile-safe-area">
      <!-- Close Button -->
      <button 
        @click="showForgotPassword = false" 
        @touchend.prevent="showForgotPassword = false"
        class="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors z-10 touch-target"
        aria-label="Close dialog"
      >
        <v-icon>mdi-close</v-icon>
      </button>

      <!-- Header -->
      <div class="text-center p-8 animate-fade-in">
        <img 
          src="/ducksImages/duckIcon.webp" 
          class="w-24 h-24 mx-auto mb-6 drop-shadow-lg transition-transform animate-float-slow" 
        />
        <h2 class="text-3xl font-bold text-gray-700  slide-in-bottom">
          Reset Password
        </h2>
        <p class="mt-2 text-gray-600  animate-fade-in" style="animation-delay: 0.2s">
          Enter your email and we'll send you a reset link
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleForgotPassword" class="p-8 pt-0 space-y-6">
        <!-- Email Field -->
        <div class="form-field animate-fade-in" style="animation-delay: 0.3s">
          <label class="block text-sm font-medium text-gray-700 ">Email</label>
          <input
            ref="forgotPasswordInputRef"
            v-model="forgotPasswordEmail"
            type="email"
            required
            class="mt-1 block w-full rounded-lg border border-gray-300  px-4 py-3 
                  bg-white  text-gray-900 
                  shadow-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="your@email.com"
          />
        </div>

        <!-- Action Buttons -->
        <div class="space-y-4 animate-fade-in" style="animation-delay: 0.4s">
          <v-btn
            block
            color="primary"
            size="large"
            :loading="isResettingPassword"
            type="submit"
            class="rounded-lg hover:scale-102 transition-transform"
          >
            <span class="relative">
              {{ isResettingPassword ? 'Sending...' : 'Send Reset Link' }}
            </span>
          </v-btn>

          <button
            type="button"
            @click="backToLogin"
            class="w-full text-center text-sm text-primary hover:text-primary-dark transition-colors hover:underline py-2"
          >
            Back to Login
          </button>
        </div>
      </form>
    </div>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { Constant } from '@/config/constants';

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  redirectUrl: {
    type: String,
    default: ''
  },
  showMessage: {
    type: String,
    default: ''
  },
  autoFocus: {
    type: Boolean,
    default: true
  }
});

// Mobile detection to prevent autofocus on mobile devices
const isMobile = computed(() => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768 && 'ontouchstart' in window) ||
         window.innerHeight < 500; // Also consider small viewport heights as mobile
});

// Touch device detection - more comprehensive
const isTouchDevice = computed(() => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
});

// Should auto-focus: disabled on mobile and touch devices to prevent keyboard issues
const shouldAutoFocus = computed(() => props.autoFocus && !isMobile.value && !isTouchDevice.value);

// Emits
const emit = defineEmits([
  'update:modelValue',
  'login-success',
  'register-success',
  'password-reset-sent',
  'notification'
]);

// Router and auth store
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
// Refs for focusing inputs inside dialogs
const emailInputRef = ref(null);
const forgotPasswordInputRef = ref(null);
const waitingBackButtonRef = ref(null);

// Dialog visibility
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Form and authentication state
const isRegisterMode = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const isLoggingIn = ref(false);
const isResettingPassword = ref(false);
const loginError = ref('');
const successMessage = ref('');
const showWaitingConfirmation = ref(false);
const showForgotPassword = ref(false);
const isResendingConfirmation = ref(false);
const showConfirmationOptions = ref(false);
const forgotPasswordEmail = ref('');

// Form data
const formData = ref({
  email: '',
  username: '',
  password: '',
  confirmPassword: ''
});

// Form validation
const emailError = ref('');
const usernameError = ref('');
const passwordError = ref('');
const confirmPasswordError = ref('');

// Watch for model value changes to reset form
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    resetForm();
    successMessage.value = props.showMessage;
    // Focus the first input when the dialog opens (mobile friendly) - only if autoFocus is enabled and not on mobile
    if (shouldAutoFocus.value) {
      nextTick(() => {
        if (!showForgotPassword.value) {
          emailInputRef.value?.focus?.();
        }
      });
    }
    
    // Add escape key listener for desktop
    if (!isMobile.value) {
      document.addEventListener('keydown', handleEscapeKey);
    }
  } else {
    // Remove escape key listener
    document.removeEventListener('keydown', handleEscapeKey);
  }
});

// Handle escape key to close dialog
const handleEscapeKey = (event) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeDialog();
  }
};

// When forgot password dialog opens, focus its email input
watch(() => showForgotPassword.value, (open) => {
  if (open) {
    nextTick(() => {
      forgotPasswordInputRef.value?.focus?.();
    });
  }
});

// When waiting confirmation dialog opens, focus the back button for accessibility
watch(() => showWaitingConfirmation.value, (open) => {
  if (open) {
    nextTick(() => {
      waitingBackButtonRef.value?.$el?.focus?.();
    });
  }
});

// Computed property for submit button disabled state
const isSubmitDisabled = computed(() => {
  if (isRegisterMode.value) {
    return !formData.value.email || 
           !formData.value.username ||
           !formData.value.password || 
           !formData.value.confirmPassword ||
           !!emailError.value ||
           !!usernameError.value ||
           !!passwordError.value ||
           !!confirmPasswordError.value;
  }
  return !formData.value.email || !formData.value.password || !!emailError.value || !!passwordError.value;
});

// Functions
const closeDialog = () => {
  // Reset all form states and clear any ongoing operations
  isLoggingIn.value = false;
  isResettingPassword.value = false;
  isResendingConfirmation.value = false;
  
  // Clear any focus traps or keyboard issues
  if (document.activeElement && document.activeElement.blur) {
    document.activeElement.blur();
  }
  
  // Close all dialogs
  showWaitingConfirmation.value = false;
  showForgotPassword.value = false;
  isOpen.value = false;
};

// Emergency close function for mobile devices
const emergencyClose = () => {
  // Force close all dialogs and reset states
  try {
    // Clear all timeouts and intervals that might be running
    const highestTimeoutId = setTimeout(() => {}, 0);
    for (let i = 0; i < highestTimeoutId; i++) {
      clearTimeout(i);
    }
    
    // Reset all reactive states
    isLoggingIn.value = false;
    isResettingPassword.value = false;
    isResendingConfirmation.value = false;
    showWaitingConfirmation.value = false;
    showForgotPassword.value = false;
    
    // Force blur any focused elements
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    
    // Hide virtual keyboard on mobile
    if (isMobile.value && document.activeElement) {
      document.activeElement.blur();
      // Additional blur for iOS Safari
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
    
    // Final close
    isOpen.value = false;
  } catch (error) {
    console.warn('Emergency close error:', error);
    // Fallback - just close the dialog
    isOpen.value = false;
  }
};

const toggleAuthMode = () => {
  isRegisterMode.value = !isRegisterMode.value;
  resetErrors();
};

const resetForm = () => {
  formData.value = { email: '', username: '', password: '', confirmPassword: '' };
  resetErrors();
  isRegisterMode.value = false;
};

const resetErrors = () => {
  loginError.value = '';
  emailError.value = '';
  usernameError.value = '';
  passwordError.value = '';
  confirmPasswordError.value = '';
  showConfirmationOptions.value = false;
};

// Get button text based on current route and mode
const getButtonText = () => {
  const isDuckPage = route.path.startsWith('/duck/');
  
  if (isDuckPage) {
    return 'Add Duck to Collection';
  }
  
  return isRegisterMode.value ? 'Start Adventure' : 'Continue Journey';
};

const showForgotPasswordDialog = () => {
  showForgotPassword.value = true;
  isOpen.value = false;
};

const backToLogin = () => {
  showWaitingConfirmation.value = false;
  showForgotPassword.value = false;
  isOpen.value = true;
};

// Validation functions
const validateEmail = () => {
  emailError.value = '';
  const email = formData.value.email.trim();
  
  if (!email) {
    return;
  }
  
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!emailRegex.test(email)) {
    emailError.value = 'Please enter a valid email address';
  }
};

const validateUsername = () => {
  usernameError.value = '';
  
  if (!formData.value.username) {
    return;
  }
  
  const username = formData.value.username.trim();
  
  if (username.length < 3) {
    usernameError.value = 'Username must be at least 3 characters long';
  } else if (username.length > 20) {
    usernameError.value = 'Username must not exceed 20 characters';
  } else if (!/^[a-zA-Z0-9_]+$/.test(username)) {
    usernameError.value = 'Username can only contain letters, numbers, and underscores';
  }
};

const validatePassword = () => {
  passwordError.value = '';
  
  if (!formData.value.password) {
    return;
  }
  
  if (isRegisterMode.value) {
    if (formData.value.password.length < 8) {
      passwordError.value = 'Password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(formData.value.password)) {
      passwordError.value = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.value.password)) {
      passwordError.value = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.value.password)) {
      passwordError.value = 'Password must contain at least one number';
    }
  }
  
  // Also validate confirm password if it exists
  if (isRegisterMode.value && formData.value.confirmPassword) {
    validateConfirmPassword();
  }
};

const validateConfirmPassword = () => {
  confirmPasswordError.value = '';
  
  if (!formData.value.confirmPassword) {
    return;
  }
  
  if (formData.value.password !== formData.value.confirmPassword) {
    confirmPasswordError.value = 'Passwords do not match';
  }
};

// Form submission
const handleSubmit = async () => {
  if (isLoggingIn.value) return;

  // Validate all fields
  validateEmail();
  if (isRegisterMode.value) {
    validateUsername();
  }
  validatePassword();
  if (isRegisterMode.value) {
    validateConfirmPassword();
  }

  // Check if there are validation errors
  if (emailError.value || passwordError.value || (isRegisterMode.value && (usernameError.value || confirmPasswordError.value))) {
    return;
  }

  try {
    isLoggingIn.value = true;
    loginError.value = '';
    showConfirmationOptions.value = false;

    if (isRegisterMode.value) {
      const result = await authStore.registerWithErrorHandling({
        email: formData.value.email,
        username: formData.value.username,
        password: formData.value.password,
        redirectUrl: props.redirectUrl
      });
      
      if (result.success) {
        isOpen.value = false;
        showWaitingConfirmation.value = true;
        // Replace emit with handler function call
        handleRegisterSuccess(formData.value.email);
      } else {
        if (result.status === 409) {
          // Handle conflict errors (email or username already exists)
          loginError.value = result.message || 'Email or username already exists';
        } else if (result.status === 400) {
          // Handle bad request errors
          loginError.value = result.message || 'Invalid input. Please check your information and try again.';
        } else {
          // Handle other errors
          loginError.value = result.message || 'Registration failed. Please try again.';
        }
      }
    } else {
      const result = await authStore.loginWithErrorHandling({
        email: formData.value.email,
        password: formData.value.password
      });
      
      if (result.success) {
        isOpen.value = false;
        handleLoginSuccess({
          email: formData.value.email,
          redirectUrl: props.redirectUrl
        });
      } else {
        if (result.status === 403) {
          loginError.value = 'Please confirm your email address before logging in.';
          showConfirmationOptions.value = true;
        } else {
          loginError.value = result.message || 'Invalid email or password';
        }
      }
    }
  } catch (error) {
    console.error('Auth error:', error);
    loginError.value = error.response?.data?.message || 'An error occurred. Please try again.';
  } finally {
    isLoggingIn.value = false;
  }
};

// Handle resend confirmation email
const handleResendConfirmation = async () => {
  if (isResendingConfirmation.value) return;

  try {
    isResendingConfirmation.value = true;
    
    // Get the email from the form or waiting confirmation screen
    const emailToUse = formData.value.email.trim();
    
    if (!emailToUse) {
      // Replace emit with handler function call
      showNotificationMessage({
        message: 'Please enter your email address first',
        color: 'error'
      });
      return;
    }
    
    // Make API call to the specified endpoint
    const requestData = { email: emailToUse };
    if (props.redirectUrl) {
      requestData.redirectUrl = props.redirectUrl;
    }
    
    await axios.post(`${Constant.API_URL}/resend-confirmation`, requestData);
    
    // Replace emit with handler function call
    showNotificationMessage({
      message: 'Confirmation email has been sent! Please check your inbox.',
      color: 'success'
    });
    
  } catch (error) {
    console.error('Failed to resend confirmation:', error);
    
    // Handle different error responses
    if (error.response) {
      switch (error.response.status) {
        case 404:
          // Replace emit with handler function call
          showNotificationMessage({
            message: 'Account not found with that email address.',
            color: 'error'
          });
          break;
        case 409:
          // Replace emit with handler function call
          showNotificationMessage({
            message: 'Your account is already verified. Please log in.',
            color: 'info'
          });
          break;
        case 429:
          // Replace emit with handler function call
          showNotificationMessage({
            message: 'Too many attempts. Please try again later.',
            color: 'warning'
          });
          break;
        default:
          // Replace emit with handler function call
          showNotificationMessage({
            message: error.response.data?.message || 'Failed to resend confirmation email.',
            color: 'error'
          });
      }
    } else {
      // Replace emit with handler function call
      showNotificationMessage({
        message: 'Network error. Please check your connection and try again.',
        color: 'error'
      });
    }
  } finally {
    isResendingConfirmation.value = false;
  }
};

// Add the handleForgotPassword function if it doesn't exist
const handleForgotPassword = async () => {
  if (isResettingPassword.value) return;
  
  if (!forgotPasswordEmail.value) {
    showNotificationMessage({
      message: 'Please enter your email address',
      color: 'error'
    });
    return;
  }
  
  try {
    isResettingPassword.value = true;
    
    await axios.post(`${Constant.API_URL}${Constant.FORGOT_PASSWORD}`, {
      email: forgotPasswordEmail.value
    });
    
    showForgotPassword.value = false;
    // Call the handler function instead of emitting
    handlePasswordResetSent();
    
  } catch (error) {
    console.error('Password reset failed:', error);
    showNotificationMessage({
      message: error.response?.data?.message || 'Failed to send reset link. Please try again.',
      color: 'error'
    });
  } finally {
    isResettingPassword.value = false;
  }
};

// Add notification state
const notificationMessage = ref('');
const notificationColor = ref('success');

// Handle login success
const handleLoginSuccess = ({ redirectUrl }) => {
  // Set notification message while simultaneously redirecting
  notificationMessage.value = 'Login successful!';
  notificationColor.value = 'success';
  emit('notification', { message: notificationMessage.value, color: notificationColor.value });
  
  // Emit login success event
  emit('login-success');

  // Check if we're on a duck view page - if so, don't redirect
  const isDuckViewPage = route.path.startsWith('/duck/');

  // Only redirect if we have a redirect URL AND we're not on a duck view page
  // If we're on a duck view page, stay there and let the parent component handle duck claiming
  if (redirectUrl && !isDuckViewPage) {
    const isAbsolute = /^https?:\/\//i.test(redirectUrl);
    nextTick(() => {
      if (isAbsolute) {
        // For absolute URLs (e.g., confirmation flow), use full page navigation
        window.location.href = redirectUrl;
      } else {
        // For internal routes, use router navigation
        router.push(redirectUrl);
      }
    });
  }
  // If no redirectUrl is provided or we're on a duck view page, don't navigate anywhere
  // Let the parent component handle what happens next
};

// Handle register success
const handleRegisterSuccess = () => {
  notificationMessage.value = 'Registration successful! Please check your email to confirm your account.';
  notificationColor.value = 'success';
  emit('notification', { message: notificationMessage.value, color: notificationColor.value });
};

// Handle password reset sent
const handlePasswordResetSent = () => {
  notificationMessage.value = 'Password reset link sent to your email';
  notificationColor.value = 'success';
  emit('notification', { message: notificationMessage.value, color: notificationColor.value });
};

// Handle notification
const showNotificationMessage = ({ message, color }) => {
  notificationMessage.value = message;
  notificationColor.value = color || 'info';
  emit('notification', { message: notificationMessage.value, color: notificationColor.value });
};

// Cleanup event listeners on component unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});
</script>

<style scoped>
.animate-float {
  animation: float 3s ease-in-out infinite;
  will-change: transform;
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .animate-float {
    animation: float 3s ease-in-out infinite;
    max-width: 200px;
    max-height: 200px;
  }
}

/* Focus states for accessibility */
a:focus, button:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

/* Reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .animate-float {
    animation: none;
  }
  
  * {
    transition-duration: 0.1s !important;
  }
}

/* Replace coral color classes with yellow that match the duck theme */
.text-yellow-400 {
  color: #facc15;
}

.text-yellow-500 {
  color: #eab308;
}

.bg-yellow-500 {
  background-color: #eab308;
}

.bg-yellow-600 {
  background-color: #ca8a04;
}

.bg-yellow-500\/20 {
  background-color: rgba(234, 179, 8, 0.2);
}

.from-yellow-500\/20 {
  --tw-gradient-from: rgba(234, 179, 8, 0.2);
}

/* Scale hover effect for cards */
.hover\:scale-102:hover {
  transform: scale(1.02);
  transition: transform 0.2s ease-in-out;
}

/* Add styles for auth dialogs */
.animate-bounce-slow {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(-5%); }
  50% { transform: translateY(0); }
}

/* New animations and styles for enhanced login dialog */
.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.5s forwards;
}

.slide-in-bottom {
  opacity: 0;
  transform: translateY(20px);
  animation: slideInBottom 0.5s forwards;
}

.slide-in-left {
  opacity: 0;
  transform: translateX(-10px);
  animation: slideInLeft 0.3s forwards;
}

.animate-pulse-slow {
  animation: pulseSlow 2s infinite;
}

.shake-animation {
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-float-slow {
  animation: floatSlow 6s ease-in-out infinite;
}

.animate-bounce-slow {
  animation: bounceSlow 2s ease-in-out infinite;
}

.message-bubble {
  position: relative;
  background-color: rgba(234, 179, 8, 0.1);
  border-radius: 1rem;
  padding: 1rem;
}

.message-bubble:before {
  content: '';
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 10px 10px 10px;
  border-style: solid;
  border-color: transparent transparent rgba(234, 179, 8, 0.1) transparent;
}

.wave-animation {
  opacity: 0;
  transform: translateX(-100%);
  animation: wave 2s ease-in-out infinite;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulseSlow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

@keyframes floatSlow {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
}

@keyframes bounceSlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes wave {
  0% {
    opacity: 0.3;
    transform: translateX(-100%);
  }
  50% {
    opacity: 0.2;
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
}

/* Improved form styles */
.form-field {
  position: relative;
  transition: all 0.3s ease;
}

.form-field:focus-within label {
  color: #eab308;
}

/* Hover effects */
input:hover {
  border-color: rgba(234, 179, 8, 0.5);
}


/* Reduced motion preference support */
@media (prefers-reduced-motion: reduce) {
  .animate-fade-in,
  .slide-in-bottom,
  .slide-in-left,
  .animate-pulse-slow,
  .shake-animation,
  .animate-float,
  .animate-float-slow,
  .animate-bounce-slow,
  .wave-animation {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}

/* Add new animations */
.animate-float-delay {
  animation: float 3s ease-in-out infinite;
  animation-delay: 1s;
}

.animate-float-reverse {
  animation: floatReverse 3s ease-in-out infinite;
  animation-delay: 2s;
}

@keyframes floatReverse {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(-5deg); }
}

.animate-count {
  animation: countUp 2s ease-out forwards;
  opacity: 0;
}

@keyframes countUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile scrolling fix */
.mobile-dialog-scroll {
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  scrollbar-width: thin;
  scrollbar-color: rgba(203, 213, 225, 0.5) transparent;
  overscroll-behavior: contain; /* Prevent scroll chaining on mobile */
}

.mobile-dialog-scroll::-webkit-scrollbar {
  width: 6px;
}

.mobile-dialog-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.mobile-dialog-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(203, 213, 225, 0.5);
  border-radius: 20px;
  border: transparent;
}

/* Mobile safe area and positioning */
.mobile-safe-area {
  position: relative;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  min-height: fit-content;
}

/* Touch-friendly targets */
.touch-target {
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

/* Prevent iOS bounce scroll */
@supports (-webkit-touch-callout: none) {
  .mobile-dialog-scroll {
    overscroll-behavior-y: none;
    -webkit-overflow-scrolling: touch;
  }
}

/* Make form more mobile-friendly */
@media (max-width: 640px) {
  .p-8 {
    padding: 1.5rem;
  }
  
  input, button {
    font-size: 16px; /* Prevents iOS zoom on input */
    touch-action: manipulation;
  }
  
  /* Prevent iOS input zoom */
  input[type="email"],
  input[type="password"],
  input[type="text"] {
    font-size: 16px; /* Min font size to prevent zoom on iOS */
    -webkit-appearance: none;
    appearance: none;
    border-radius: 8px;
  }
  
  /* Better mobile button sizing */
  .v-btn {
    min-height: 48px;
    touch-action: manipulation;
  }
  
  /* Improve close button on mobile */
  .touch-target {
    min-width: 48px;
    min-height: 48px;
  }
}

/* Prevent viewport zoom on focus */
@media screen and (max-width: 768px) {
  input[type="text"],
  input[type="email"],
  input[type="password"],
  textarea,
  select {
    font-size: 16px !important;
    transform-origin: left top;
    transition: none;
  }
}

/* Improve modal positioning on small screens */
@media (max-height: 600px) {
  .mobile-safe-area {
    max-height: 95vh;
    margin: 2.5vh auto;
  }
}
</style>